// src/features/bpch/shampoo/ShampooPage.jsx
import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, useTheme, Dialog, DialogContent, Paper } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import FooterSection from "../../../shared/sections/FooterSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ShampooResultsSection from "./results/ShampooResultsSection";
import ProgressiveAnalysisTracker from "../../fb/cookies/ProgressiveAnalysisTracker";
import ShampooQuestions from "./questions/ShampooQuestions";
import ShampooPlayBook from "./playbook/ShampooPlayBook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { theme } from "../../../design/theme";
import { apiFetch } from "../../../api/apiClient";

const { perlColors, perlTypography } = theme.custom;

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "packaging", "ohp"];
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};

const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    return Number.isFinite(Number(value));
  });

  entries.sort((a, b) => {
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([key, value]) => {
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({ assumptions = {} }) {
  return (
    <Box>
      {assumptions && Object.keys(assumptions).length > 0 && (
        <Paper
          sx={{
            p: 3,
            bgcolor: perlColors.surface,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "18px",
              fontFamily: perlTypography.family,
              letterSpacing: "0.3px",
              color: perlColors.textPrimary,
            }}
          >
            Assumptions
          </Typography>

          {/* Render each subsection if available */}
          {assumptions.Formulation && assumptions.Formulation.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Formulation
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Formulation.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.Process && assumptions.Process.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Process
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Process.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.Packaging && assumptions.Packaging.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Packaging
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Packaging.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.OHP && assumptions.OHP.length > 0 && (
            <Box sx={{ mb: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                OHP
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.OHP.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default function ShampooPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const commodity = searchParams.get("commodity") || "Shampoos";

  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [assumptionsData, setAssumptionsData] = useState(null);
  const [keyInsightsData, setKeyInsightsData] = useState(null);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  // Track which section was last modified
  const [lastModifiedSection, setLastModifiedSection] = useState(null);

  // Auto-enable sections when data is populated
  React.useEffect(() => {
    if (playBookData && Object.keys(playBookData).length > 0) {
      setSectionStarted((prev) => ({ ...prev, playbook: true }));
    }
  }, [playBookData]);

  React.useEffect(() => {
    if (recipeData && recipeData.length > 0) {
      setSectionStarted((prev) => ({ ...prev, formulation: true }));
    }
  }, [recipeData]);

  React.useEffect(() => {
    if (costData) {
      setSectionStarted((prev) => ({ ...prev, results: true }));
    }
  }, [costData]);

  // Functional groups for shampoo formulation
  const functionalGroups = [
    "Surfactants",
    "Conditioning agents",
    "Thickeners",
    "Preservatives",
    "Fragrances",
    "pH Adjusters",
    "Active ingredients",
    "Chelating agents",
    "Colorants",
    "Pearling agents",
    "Botanical extracts",
    "Proteins",
    "Vitamins",
    "Other",
  ];

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
    setLastModifiedSection("product");
  };

  const handlePlayBookChange = (field, value) => {
    setPlayBookData({ ...playBookData, [field]: value });
    setLastModifiedSection("playbook");
  };

  const getTimestampId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please complete product information first");
      return;
    }

    console.log("🚀 Starting Shampoo Progressive Quick Cost Analysis with SSE...");

    // Initialize progress steps
    const totalSteps = 14;
    const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Processing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);

    let maxProgress = 0;

    try {
      // const projectId = `SHAMPOO_${getTimestampId()}`;
      // const partKey = `${productData.productName.replace(/\s+/g, "_")}_${Date.now()}`;

      const projectId = `PROJ_1764775036995`;
      const partKey = `PART_1764775036995`;

      const payload = {
        productName: productData.productName,
        country: productData.manufacturingCountry,
        skuWeight: parseFloat(productData.skuWeight),
        skuWeightUnit: productData.skuWeightUnit,
        marketPositioning: productData.marketPositioning,
        annualVolume: productData.annualVolume,
        projectId: projectId,
        partKey: partKey,
      };

      console.log(
        "📤 Sending payload to /api/bpch/shampoo/execute-complete-analysis-stream:",
        payload
      );

      const response = await apiFetch("/api/bpch/shampoo/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const jsonStr = line.slice(6);
            try {
              const event = JSON.parse(jsonStr);

              if (event.type === "progress") {
                const progress = Math.round((event.step / event.total) * 100);
                maxProgress = Math.max(maxProgress, progress);
                setOverallProgress(maxProgress);
                setCurrentProgressStep(event.step);

                const updatedSteps = [...initialSteps];
                if (event.step <= updatedSteps.length) {
                  updatedSteps[event.step - 1] = {
                    ...updatedSteps[event.step - 1],
                    label: event.label || `Step ${event.step}`,
                    status: event.status || "success",
                    message: event.message || "",
                  };
                  setProgressSteps(updatedSteps);
                }
              } else if (event.type === "complete") {
                console.log("✅ Analysis complete:", event);

                // Extract data from response
                if (event.FormulaData) {
                  setRecipeData(event.FormulaData);
                  setSectionStarted((prev) => ({ ...prev, formulation: true }));
                }

                if (event.playBookData) {
                  // Backend wraps data in extra layer, unwrap it
                  const unwrappedPlayBookData =
                    event.playBookData.playBookData || event.playBookData;
                  console.log("🎯 Setting playbook data:", unwrappedPlayBookData);
                  console.log("🔑 Playbook data keys:", Object.keys(unwrappedPlayBookData));
                  setPlayBookData(unwrappedPlayBookData);
                  setSectionStarted((prev) => ({ ...prev, playbook: true }));
                }

                if (event.summary) {
                  setCostData({
                    breakdown: {
                      material: event.summary.material_cost,
                      process: event.summary.process_cost,
                      packaging: event.summary.packaging_cost,
                      ohp: event.summary.ohp_cost,
                      total_should_cost: event.summary.total_cost,
                    },
                  });
                }

                if (event.riskData) setRiskData(event.riskData);
                if (event.substituteData) setSubstituteData(event.substituteData);
                if (event.productImage?.imageUrl) setProductImageUrl(event.productImage.imageUrl);
                if (event.assumptions) setAssumptionsData(event.assumptions);
                if (event.KeyInsightsJson) setKeyInsightsData(event.KeyInsightsJson);

                setOverallProgress(100);
                setSectionStarted((prev) => ({ ...prev, results: true }));

                setTimeout(() => {
                  setShowProgressTracker(false);
                  setActiveSection("results");
                }, 1500);
              } else if (event.type === "error") {
                console.error("❌ Error:", event.message);
                alert(`Analysis failed: ${event.message}`);
                setShowProgressTracker(false);
              }
            } catch (e) {
              console.error("Failed to parse SSE event:", e);
            }
          }
        }
      }
    } catch (error) {
      console.error("❌ Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setShowProgressTracker(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description: `Answer a few quick questions about your ${commodity.toLowerCase()} product to generate an AI-driven cost analysis with formulation, manufacturing parameters, and detailed insights.`,
    },
    {
      id: "formulation",
      label: "Formulation",
      icon: <ScienceIcon />,
      disabled: false,
      title: "Ready to Build Formulation",
      description: "View and edit ingredient composition with functional groups and percentages.",
    },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: false,
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: false,
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

  const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);

  const costTotal = useMemo(() => {
    const declaredTotal = costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="BPCH"
          industry="Personal Care"
          commodity={commodity}
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height: "92%" }}>
                <ShampooQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  initialData={productData}
                  commodity={commodity}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  functionalGroups={functionalGroups}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <ShampooPlayBook data={playBookData} onChange={handlePlayBookChange} />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate formula first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ShampooResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    assumptions={assumptionsData || {}}
                    keyInsights={keyInsightsData || null}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: assumptionsData || {},
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {sectionStarted.product && (
          <QuickCostButton
            onClick={handleProgressiveQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
